import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';

/*
 * The way a detector gets measured on a real phone (docs/DEVICE_DETECTOR.md): in the Field camera's Pro drawer, shown
 * only where the app ships a model, and a person turns it on, since that fetches files once. It reports how long a
 * run takes on this device, which is what the manifest's `phones` asks for, and draws what it finds.
 *
 * What it draws is a box, never a name: it says nothing about what the thing is, and it isn't a check on anything.
 */
const MB = 1024 * 1024;
const FAILURES = new Set(['hash_mismatch', 'no_secure_context', 'fetch_failed', 'load_failed', 'no_canvas', 'run_failed',
  'not_supported', 'no_runtime', 'worker_failed', 'stalled', 'frame_refused']);

/** A box in fractions of the frame, as a share of a box the frame fills with object-fit: cover, which crops it. */
export function coverBox(box, videoW, videoH, shownW, shownH) {
  const scale = Math.max(shownW / videoW, shownH / videoH);
  const offsetX = (shownW - videoW * scale) / 2;
  const offsetY = (shownH - videoH * scale) / 2;
  return {
    left: (box.x1 * videoW * scale + offsetX) / shownW,
    top: (box.y1 * videoH * scale + offsetY) / shownH,
    width: ((box.x2 - box.x1) * videoW * scale) / shownW,
    height: ((box.y2 - box.y1) * videoH * scale) / shownH,
  };
}

/** Over the picture, while a trial runs. Takes no touches. */
export function DetectorBoxes({ boxes, videoRef }) {
  const { t } = useTranslation('camera');
  const video = videoRef.current;
  const shown = video?.getBoundingClientRect?.();
  if (!boxes?.length || !video?.videoWidth || !shown?.width) return null;
  return (
    <div data-testid="detector-boxes" aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {boxes.map((box) => {
        const at = coverBox(box, video.videoWidth, video.videoHeight, shown.width, shown.height);
        return (
          <div key={`${box.x1}:${box.y1}:${box.x2}:${box.y2}`} data-testid="detector-box" style={{
            position: 'absolute',
            left: `${at.left * 100}%`,
            top: `${at.top * 100}%`,
            width: `${at.width * 100}%`,
            height: `${at.height * 100}%`,
            border: '2px dashed #fff',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.6)',
            color: '#fff',
            fontSize: 12,
          }}>
            <span style={{ background: 'rgba(0,0,0,0.6)', padding: '0 4px' }}>
              {t('detector.box', { percent: Math.round(box.score * 100) })}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function DetectorTrial({ detector, on, onToggle, manifest }) {
  const { t } = useTranslation('camera');
  const { state, reason, warmMs, summary } = detector;
  if (state === 'absent') return null;
  const size = Math.ceil((manifest.model.bytes || 0) / MB);
  return (
    <section data-testid="detector-trial" data-state={state}>
      <h3 style={{ margin: '12px 0 4px', fontSize: 14 }}>{t('detector.title')}</h3>
      <p style={{ margin: '0 0 8px', fontSize: 13 }}>{t('detector.note', { size })}</p>
      {manifest.trial && <p data-testid="detector-untrained" style={{ margin: '0 0 8px', fontSize: 13 }}>{t('detector.untrained')}</p>}
      <Button variant="secondary" data-testid="detector-toggle" aria-pressed={on} onClick={() => onToggle(!on)}>
        {on ? t('detector.stop') : t('detector.start')}
      </Button>
      <div role="status" data-testid="detector-status" style={{ marginTop: 8, fontSize: 13 }}>
        {state === 'failed' && t(`detector.failed.${FAILURES.has(reason) ? reason : 'other'}`)}
        {state !== 'failed' && state !== 'off' && on && t(`detector.state.${state}`)}
        {state === 'running' && warmMs !== undefined && ` ${t('detector.warm', { ms: warmMs })}`}
      </div>
      {summary.runs > 0 && (
        <p data-testid="detector-summary" style={{ margin: '4px 0 0', fontSize: 13 }}>
          {t('detector.summary', { runs: summary.runs, skipped: summary.skipped, p50: summary.p50, p95: summary.p95 })}
        </p>
      )}
    </section>
  );
}
